import axios from 'axios'
import * as React from 'react'
import { createElement } from 'react'
import ReactMarkdown from 'react-markdown'

import { serverLogger } from '../Global/ServerLogger'
import { Translation as Tr } from '../Global/Translation'
import { MissingString, TranslationInterface, TranslationsInterface } from '../Interfaces/TranslationsInterface'
import './Translation.scss'

const translationsHost: string = window.initialConfiguration
  ? window.initialConfiguration.endpoints.translations
  : 'https://test-translations.zerv.me'
export const versionInfo = axios.get<{ lastUpdated: string }>(`${translationsHost}/info.json?_=${new Date().getTime()}`)

interface Replacements {
  [key: string]: string
}

export class Translation {
  private static loaded = false
  private static translations: TranslationInterface = {}
  private static missingTranslationStrings: MissingString[] = []
  public static locale = ''

  public static get hasLoaded() {
    return this.loaded
  }

  public static get all(): TranslationInterface {
    return this.translations
  }

  public static missingTranslationKeysLogger(): void {
    let previousLength = 0
    setInterval(() => {
      if (previousLength !== this.missingTranslationStrings.length) {
        serverLogger(this.missingTranslationStrings)
        previousLength = this.missingTranslationStrings.length
      }
    }, 3000)
  }

  public static l(key: string, prefix?: string, replacements?: Replacements): string {
    if (!this.loaded) {
      return key
    } else {
      if (this.translations[key]) {
        return Translation.replaceValues(this.translations[key], replacements)
      } else {
        if (prefix) {
          return Translation.replaceValues(this.translations[this.getKeyByPrefix(key, prefix)], replacements)
        }
        const missingKeyInfo = {
          name: 'Missing translation string',
          key,
          locale: this.locale,
          url: window.location.href,
        }

        const keyDuplicates = this.missingTranslationStrings.filter((str) => str.key === key)
        if (keyDuplicates.length === 0) {
          this.missingTranslationStrings.push(missingKeyInfo)
        }

        return `[${key}]`
      }
    }
  }

  public static setReplacements(str: string, replacements?: Replacements) {
    if (!replacements) {
      return str
    }

    return str.replace(/%\w+%/g, (all) => {
      all = all.replaceAll('%', '')
      return replacements[all] || all
    })
  }

  public static replaceValues(text = '', replacements?: Replacements): string {
    text = Translation.setReplacements(text, replacements)

    text = text.replace('</br>', '\n').replace('<br>', '\n')
    return text.replace(/\[BRAND\]/g, (a, b) => {
      return window.initialConfiguration.brand || ''
    })
  }

  public static md(key: string) {
    return <ReactMarkdown source={this.translations[key] || key} />
  }

  public static tsxl(key: string, prefix?: string): JSX.Element {
    if (!prefix) {
      return createElement(Tr, {
        translationKey: key,
      })
    } else {
      return createElement(Tr, {
        translationKey: this.getKeyByPrefix(key, prefix),
      })
    }
  }

  private static getKeyByPrefix(key: string, prefix: string) {
    const pfKey = prefix + '.' + key
    const defaultPfKey = 'invoice.' + key
    if (this.translations[pfKey]) {
      return pfKey
    } else if (this.translations[defaultPfKey]) {
      return defaultPfKey
    } else {
      return defaultPfKey
    }
  }

  /**
   * Loads translations for given locale
   * This function needs to have a special format that will make it callable from legacy side,
   * hence getTranslationsHost cant be used here
   *
   * @param locale
   * @param useCache
   */
  public static async load(locale: string) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const translations = await axios.get(
        `${translationsHost}/lang_${locale}.json?_=${(await versionInfo).data.lastUpdated}`,
      )
      Translation.payload = translations.data
      resolve(translations.data)
    })
  }

  // eslint-disable-next-line no-async-promise-executor
  public static loadInvoiceTranslations = new Promise(async (resolve) => {
    const invoice = await axios.get(`${translationsHost}/invoice.json?_=${(await versionInfo)?.data?.lastUpdated}`)
    resolve(invoice?.data)
  })

  // eslint-disable-next-line accessor-pairs
  public static set payload(translations: TranslationInterface) {
    this.translations = translations
    this.loaded = true
  }
}

export function l(key: string, prefix?: string, replacements?: Replacements): string {
  return Translation.l(key, prefix, replacements)
}

export function tsxl(key: string, prefix?: string): JSX.Element {
  return Translation.tsxl(key, prefix)
}

/* Multiline translation */
export function ml(key: string): string {
  return (l(key) || '').replace(/\<br\>/g, '\n')
}

/* Returns a list of translations with the same key + index
 *
 * example:
 * translation0: 'foo',
 * translation1: 'bar'
 * tlist('translation') returns ['foo', 'bar']
 *
 * Loop stops when no translation for index is found and l(key) returns [key]
 * Max list size of 10 is just a precaution.
 */
export function tlist(key: string): string[] {
  let index = 0
  const maxListSize = 10
  const list = []
  while (l(key + index) !== `[${key + index}]` && index < maxListSize) {
    list.push(l(key + index))
    index++
  }
  if (list.length === 0) {
    list.push(`[${key}]`)
  }
  return list
}

export function reactMD(
  key: string,
  opts?: {
    inline?: boolean
    replacements?: { [key: string]: any }
  },
) {
  let result = Translation.l(key)
  opts = opts || {}
  if (opts.replacements) {
    for (const key in opts.replacements) {
      result = result.replace(new RegExp(key), opts.replacements[key], 'ig')
    }
  }
  const classes = ['__react-md-wrapper']
  if (opts.inline) {
    classes.push('__react-md-wrapper--inline')
  }
  return (
    <span className={classes.join(' ')}>
      <ReactMarkdown
        renderers={{
          link: (props) => (
            <a href={props.href} target="_blank">
              {props.children}
            </a>
          ),
        }}
        source={result}
      />
    </span>
  )
}

window.loadTranslations = Translation.load
window.loadInvoiceTranslations = Translation.loadInvoiceTranslations
window.versionInfo = versionInfo
